/**
 * severity.ts — PPP Stream A/B2, DLD §A9
 *
 * Collapses 5 legacy levels to 3 geography-aligned tiers.
 * Mirror of backend/src/lib/severity.py — keep in sync.
 *
 * Thresholds:
 *   daysUntil < 0            → red   (overdue)
 *   0 ≤ daysUntil ≤ 30       → red   (due soon — action required this month)
 *   31 ≤ daysUntil ≤ 60      → orange (upcoming — plan ahead)
 *   daysUntil > 60           → green (on track)
 *   date is null             → none
 *
 * Text colors use WCAG AA-compliant darker shades (E10):
 *   red text   → text-red-700 (#b91c1c) — 6.5:1 on white
 *   orange text → text-orange-700 (#c2410c) — 5.4:1 on white
 *   green text  → text-green-700 (#166534) — passes AA
 */

/**
 * Renewal severity thresholds (days until expiry). Single source of truth for
 * the frontend; the backend mirror lives in backend/src/lib/severity.py.
 *   daysUntil <= RENEWAL_DUE_SOON_DAYS → red    (overdue or due this month)
 *   daysUntil <= RENEWAL_UPCOMING_DAYS → orange (plan ahead)
 *   otherwise                          → green  (on track)
 * RENEWAL_TIMELINE_WINDOW_DAYS is the horizon of the 90-day dot-timeline.
 */
export const RENEWAL_DUE_SOON_DAYS = 30;
export const RENEWAL_UPCOMING_DAYS = 60;
export const RENEWAL_TIMELINE_WINDOW_DAYS = 90;

export type SeverityLevel = "red" | "orange" | "green" | "none";

export interface Severity {
  level: SeverityLevel;
  daysUntil: number | null;
  /** Accent colour (for dots/icons — NOT for text directly). */
  color: string;
  bgClass: string;
  /** Text colour (WCAG AA-compliant dark shade). */
  textClass: string;
  label: string;
}

export function computeSeverity(date: Date | null): Severity {
  if (!date) {
    return {
      level: "none",
      daysUntil: null,
      color: "transparent",
      bgClass: "",
      textClass: "text-text-dim",
      label: "",
    };
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);

  const diffMs = target.getTime() - now.getTime();
  const daysUntil = Math.ceil(diffMs / 86_400_000);

  if (daysUntil <= RENEWAL_DUE_SOON_DAYS) {
    return {
      level: "red",
      daysUntil,
      color: "#f87171",
      bgClass: "bg-red-500/15",
      textClass: "text-red-700",
      label: "Due soon",
    };
  }
  if (daysUntil <= RENEWAL_UPCOMING_DAYS) {
    return {
      level: "orange",
      daysUntil,
      color: "#f0883e",
      bgClass: "bg-orange-400/15",
      textClass: "text-orange-700",
      label: "Upcoming",
    };
  }
  return {
    level: "green",
    daysUntil,
    color: "#2ea043",
    bgClass: "bg-green-500/15",
    textClass: "text-green-700",
    label: "On track",
  };
}

/** Human-readable "in X days" / "X days ago" / "today" string. */
export function daysLabel(daysUntil: number): string {
  if (daysUntil === 0) return "today";
  if (daysUntil > 0) return `in ${daysUntil} day${daysUntil === 1 ? "" : "s"}`;
  return `${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? "" : "s"} ago`;
}
